<template>
  <section
    class="cr-list us-list"
    :class="{ 'us-list--no-type': isAdminTab }"
    aria-label="Team members"
  >
    <div class="cr-list__head">
      <div class="cr-list__columns us-list__columns" aria-hidden="true">
        <span>Member</span>
        <span v-if="!isAdminTab">Type</span>
        <span>Contact</span>
        <span>Role</span>
        <span>Status</span>
        <span>Joined</span>
        <span>Actions</span>
      </div>
    </div>

    <div class="cr-list__scroll">
      <div v-if="loading" class="cr-list__body">
        <div v-for="n in 8" :key="n" class="cr-row us-row cr-row--skeleton" />
      </div>

      <div v-else-if="users.length" class="cr-list__body" role="list">
        <UsersRow
          v-for="user in users"
          :key="user.id"
          :user="user"
          :hide-type="isAdminTab"
          :current-user-id="currentUserId"
          @edit="$emit('edit', $event)"
          @delete="$emit('delete', $event)"
          @reset-password="$emit('reset-password', $event)"
          @toggle-status="$emit('toggle-status', $event)"
          @set-extension="$emit('set-extension', $event)"
        />
      </div>

      <div v-else-if="isSearchActive" class="cr-empty">
        <v-icon icon="mdi-magnify-close" size="40" class="cr-empty__icon" />
        <p class="cr-empty__title">No matching members</p>
        <p class="cr-empty__subtitle">Try another search or clear the app bar filter.</p>
      </div>

      <div v-else class="cr-empty">
        <v-icon :icon="emptyIcon" size="40" class="cr-empty__icon" />
        <p class="cr-empty__title">{{ emptyTitle }}</p>
        <p class="cr-empty__subtitle">{{ emptySubtitle }}</p>
        <v-btn
          variant="flat"
          class="us-empty__add"
          prepend-icon="mdi-plus"
          @click="$emit('add')"
        >
          Add Admin/Agent
        </v-btn>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import UsersRow from '@/components/Users/UsersRow.vue'
import { AGENT_TYPE, USER_ROLE } from '@/utils/orgUsers'

defineOptions({ name: 'UsersList' })

const props = defineProps({
  users: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  isSearchActive: { type: Boolean, default: false },
  roleTab: { type: String, default: USER_ROLE.ADMIN },
  agentTypeTab: { type: String, default: AGENT_TYPE.AI },
  currentUserId: { type: String, default: '' },
})

defineEmits([
  'add',
  'edit',
  'delete',
  'reset-password',
  'toggle-status',
  'set-extension',
])

const isAdminTab = computed(() => props.roleTab === USER_ROLE.ADMIN)
const isHumanTab = computed(() => props.agentTypeTab === AGENT_TYPE.HUMAN)

const emptyIcon = computed(() => {
  if (isAdminTab.value) return 'mdi-shield-account-outline'
  return isHumanTab.value ? 'mdi-account-outline' : 'mdi-robot-outline'
})

const emptyTitle = computed(() => {
  if (isAdminTab.value) return 'No admins yet'
  return isHumanTab.value ? 'No human agents yet' : 'No AI agents yet'
})

const emptySubtitle = computed(() => {
  if (isAdminTab.value) {
    return 'Invite an admin with email and phone details.'
  }
  return isHumanTab.value
    ? 'Invite a human teammate with email and phone details.'
    : 'Create your first AI agent with a display name.'
})
</script>
